// app/features/posthog/services_and_hooks/posthogClientService.ts
"use client";

import posthog from "posthog-js";

import {
  type EventProperties,
  POSTHOG_EVENTS,
  type PostHogConfig,
} from "../posthog.interface";

class PostHogClientService {
  private isInitialized = false;

  /**
   * Initialize PostHog client
   */
  init(config: PostHogConfig): void {
    if (typeof window === "undefined") {
      console.warn(
        "PostHog client should only be initialized on the client side",
      );
      return;
    }

    if (this.isInitialized) {
      console.warn("PostHog client is already initialized");
      return;
    }

    posthog.init(config.apiKey, {
      api_host: config.apiHost || "/ingest",
      ui_host: config.uiHost || "https://us.posthog.com",
      capture_pageview: config.capturePageview ?? false,
      capture_pageleave: config.capturePageleave ?? true,
      capture_exceptions: config.captureExceptions ?? true,
      debug: false, // Always disable debug
      disable_session_recording: true, // Disable session recording to prevent snapshots
      loaded: (posthog) => {
        // Remove debug logging
      },
    });

    this.isInitialized = true;
  }

  /**
   * Track an event with properties
   */
  track(eventName: string, properties?: EventProperties): void {
    if (!this.isInitialized) {
      // Silently skip tracking if PostHog is not initialized
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    posthog.capture(eventName, properties);
  }

  /**
   * Identify a user
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn("PostHog client is not initialized. Call init() first.");
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    posthog.identify(userId, properties);
  }

  /**
   * Set user properties
   */
  setPersonProperties(properties: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn("PostHog client is not initialized. Call init() first.");
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    posthog.setPersonProperties(properties);
  }

  /**
   * Reset user session
   */
  reset(): void {
    if (!this.isInitialized) {
      console.warn("PostHog client is not initialized. Call init() first.");
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    posthog.reset();
  }

  /**
   * Capture page view
   */
  capturePageView(properties?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn("PostHog client is not initialized. Call init() first.");
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    posthog.capture("$pageview", properties);
  }

  /**
   * Get the PostHog instance (for advanced usage)
   */
  getInstance() {
    if (typeof window === "undefined") {
      return null;
    }
    return posthog;
  }

  /**
   * Check if a feature flag is enabled for the current user
   */
  isFeatureEnabled(flagName: string): boolean | undefined {
    if (!this.isInitialized) {
      console.warn("PostHog client is not initialized. Call init() first.");
      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    const instance = this.getInstance();
    if (!instance) {
      return undefined;
    }

    try {
      return instance.isFeatureEnabled(flagName);
    } catch (error) {
      console.error("Error checking feature flag:", error);
      return undefined;
    }
  }

  /**
   * Check if PostHog is initialized
   */
  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Shutdown PostHog and cleanup (useful for development)
   */
  shutdown(): void {
    if (!this.isInitialized) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    // Stop capturing and clean up
    posthog.opt_out_capturing();
    posthog.stopSessionRecording();

    this.isInitialized = false;
  }
}

// Export singleton instance
export const posthogClientService = new PostHogClientService();

// Export event constants for convenience
export { POSTHOG_EVENTS };
